import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { YoutubeLogoIcon } from "@phosphor-icons/react";
import avrahLogo from "../../Assets/avrah-horizontal-white.png";
import { SecondaryCTA } from "../ui";
import { localizedPath } from "../../utils/seoLinks";

// Dark-background HomeSeTv wordmark, served as a static asset (CRA's SVGR
// transform rejects this logo's namespace tags, so it isn't imported).
const HSV_LOGO = `${process.env.PUBLIC_URL}/hsv-logo-white.svg`;

const linkSx = {
  "&&": {
    color: "rgba(255,255,255,0.55)",
    fontSize: "0.8rem",
    fontWeight: 400,
    textDecoration: "none",
    "&:hover": { color: "#B8860B", textDecoration: "underline" },
    "&:visited": { color: "rgba(255,255,255,0.55)" },
    transition: "color 0.2s ease",
  },
};

const Footer = ({ className }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentLang = location.pathname === "/en" || location.pathname.startsWith("/en/") ? "en" : "it";
  const localize = (path) => localizedPath(path, currentLang);

  // A page with its own FAQ (the homepage, /plans) scrolls to it instead
  // of sending the visitor to another page's questions. Elsewhere the link
  // leads to the FAQ on /plans.
  const handleFaqClick = (e) => {
    const target = document.getElementById("faq");
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `${location.pathname}#faq`);
  };

  return (
    <Box
      component="footer"
      className={className}
      sx={{
        position: "relative",
        zIndex: 2,
        background: "#0a0a0a",
        py: { xs: 5, md: 6 },
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          alignItems: "start",
          gap: { xs: 4, md: 8 },
        }}
      >
        {/* Left: wordmark, download and YouTube. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            gap: 2,
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <Box
            component="img"
            src={avrahLogo}
            alt="A.V.R.A.H."
            sx={{ height: 24, width: "auto", display: "block" }}
          />
          <SecondaryCTA size="md" component={Link} to={localize("/download")} sx={{ minHeight: 48 }}>
            {t("footer.downloadApp")}
          </SecondaryCTA>
          <Box
            component="a"
            href="https://www.youtube.com/@A.V.R.A.H"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="YouTube"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 44,
              minHeight: 44,
              "&&": { color: "#FF0000", "&:hover": { color: "#cc0000" } },
              transition: "color 0.2s ease",
            }}
          >
            <YoutubeLogoIcon size={26} />
          </Box>
        </Box>

        {/* Middle: legal links */}
        <Box
          component="nav"
          aria-label="Legal"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            gap: 1.25,
          }}
        >
          <Typography component={Link} to={localize("/terms")} sx={linkSx}>
            {t("footer.termsOfService")}
          </Typography>
          <Typography component={Link} to={localize("/referral-terms")} sx={linkSx}>
            {t("footer.referralTerms")}
          </Typography>
          <Typography component={Link} to={localize("/privacy")} sx={linkSx}>
            {t("footer.privacyPolicy")}
          </Typography>
          <Typography component={Link} to={localize("/cookie-policy")} sx={linkSx}>
            {t("footer.cookiePolicy")}
          </Typography>
        </Box>

        {/* Right: support — FAQ first, contact as fallback */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            gap: 1.25,
          }}
        >
          <Typography
            component={Link}
            to={`${localize("/plans")}#faq`}
            onClick={handleFaqClick}
            sx={linkSx}
          >
            {t("plans.faq.title")}
          </Typography>
          <Typography component={Link} to={localize("/press")} sx={linkSx}>
            {t("footer.press")}
          </Typography>
          <Typography
            component="a"
            href="mailto:info@homesetv.com"
            sx={linkSx}
          >
            {t("plans.faq.contactSupport")}
          </Typography>
        </Box>
      </Container>

      {/* Parent-brand signature */}
      <Container maxWidth="lg" sx={{ mt: { xs: 4, md: 5 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              color: "rgba(255,255,255,0.55)",
              fontSize: "0.8rem",
              fontWeight: 400,
              lineHeight: 1.5,
              textAlign: { xs: "center", sm: "left" },
              order: { xs: 2, sm: 1 },
            }}
          >
            {t("footer.copyright", { year: new Date().getFullYear() })}
            <Box component="span" sx={{ display: "block" }}>
              {t("footer.legalAddress")}
            </Box>
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              order: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.9rem",
                fontWeight: 400,
                letterSpacing: "0.02em",
              }}
            >
              {t("footer.parentBrand")}
            </Typography>
            <Box
              component="a"
              href="https://www.homesetv.it"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="HomeSeTv"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: 44,
                opacity: 0.8,
                transition: "opacity 0.2s ease",
                "&:hover": { opacity: 1 },
              }}
            >
              <Box
                component="img"
                src={HSV_LOGO}
                alt="HomeSeTv"
                sx={{ height: 28, width: "auto", display: "block" }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
